/**
 * KCIS™ Pro — Nutrient & Functional Imbalance Indicators (Sprint 2, read-only).
 *
 * Responsibilities:
 *  - Resolves the latest Module 1 (Lite) assessment for the given clientId.
 *  - Fetches pattern results, escalation, and routing in parallel.
 *  - Renders escalation banner (if any), cluster chips, indicator cards with
 *    confidence-band styling and collapsible evidence trails, and a wellness
 *    compliance disclaimer.
 *  - Surfaces a "Compute now" CTA when no results exist yet. The CTA is
 *    gated by the backend's NUTRIENT_INDICATORS_ENABLED feature flag
 *    (super_admin can always bypass — gating is enforced server-side).
 *
 * Sprint 2 explicitly excludes:
 *  - Practitioner override UI (Sprint 4)
 *  - share_with_client toggle UI (Sprint 4)
 *  - Airtable mirror surface (Sprint 3)
 *  - AI summary integration (Sprint 4)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, adminAuth } from "@/lib/api";
import { formatApiError } from "@/lib/formatError";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Info,
  Loader2,
  RefreshCw,
  ShieldAlert,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

// ───────────────────── Confidence-band visual tokens ─────────────────────
const BAND_TOKENS = {
  low: {
    label: "Low confidence",
    chipClass: "bg-slate-100 text-slate-600 border-slate-200",
    cardAccentClass: "border-slate-200",
    barClass: "bg-slate-400",
  },
  moderate: {
    label: "Moderate confidence",
    chipClass: "bg-amber-50 text-amber-800 border-amber-200",
    cardAccentClass: "border-amber-200",
    barClass: "bg-amber-500",
  },
  high: {
    label: "High confidence",
    chipClass: "bg-purple-50 text-purple-900 border-purple-200",
    cardAccentClass: "border-purple-300",
    barClass: "bg-purple-900",
  },
};

const BAND_ORDER = { high: 0, moderate: 1, low: 2 };

const ESCALATION_TOKENS = {
  medical_evaluation: {
    Icon: ShieldAlert,
    wrapperClass: "bg-rose-50 border-rose-200 text-rose-900",
    iconClass: "text-rose-700",
    label: "Medical evaluation recommended",
  },
  practitioner_review: {
    Icon: AlertTriangle,
    wrapperClass: "bg-amber-50 border-amber-200 text-amber-900",
    iconClass: "text-amber-700",
    label: "Practitioner review recommended",
  },
};

// ───────────────────── Sub-components ─────────────────────
function EscalationBanner({ escalation }) {
  if (!escalation || escalation.recommended_escalation === "none") return null;
  const token = ESCALATION_TOKENS[escalation.recommended_escalation];
  if (!token) return null;
  const { Icon, wrapperClass, iconClass, label } = token;
  const reds = escalation.red_flags || [];
  const softs = escalation.soft_flags || [];
  return (
    <div
      data-testid="nutrient-escalation-banner"
      className={`rounded-2xl border ${wrapperClass} p-4 sm:p-5 mb-6 flex flex-col sm:flex-row gap-3 sm:items-start`}
    >
      <div className={`shrink-0 rounded-xl h-10 w-10 flex items-center justify-center bg-white/60 ${iconClass}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-display text-base sm:text-lg leading-snug" data-testid="nutrient-escalation-label">
          {label}
        </div>
        {escalation.escalation_messaging?.to_practitioner && (
          <p className="text-sm mt-1 opacity-90" data-testid="nutrient-escalation-message">
            {escalation.escalation_messaging.to_practitioner}
          </p>
        )}
        {(reds.length > 0 || softs.length > 0) && (
          <ul className="mt-3 space-y-1 text-sm">
            {reds.map((f) => (
              <li key={f.code} className="flex gap-2" data-testid={`nutrient-flag-${f.code}`}>
                <span className="font-medium">Hard flag:</span>
                <span>{f.message}</span>
              </li>
            ))}
            {softs.map((f) => (
              <li key={f.code} className="flex gap-2 opacity-90" data-testid={`nutrient-flag-${f.code}`}>
                <span className="font-medium">Soft flag:</span>
                <span>{f.message}</span>
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs mt-3 opacity-80">
          Escalation is routed to the practitioner first. Clients are not auto-notified.
        </p>
      </div>
    </div>
  );
}

function ClusterStrip({ clusters }) {
  const triggered = (clusters || []).filter((c) => c.triggered);
  if (triggered.length === 0) return null;
  return (
    <div className="mb-5" data-testid="nutrient-cluster-strip">
      <div className="kcis-overline text-purple-900 mb-2">Active wellness clusters</div>
      <div className="flex flex-wrap gap-2">
        {triggered.map((c) => (
          <span
            key={c.code}
            data-testid={`nutrient-cluster-${c.code}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-50 text-purple-900 border border-purple-200 text-xs"
            title={c.compliance_language || c.name}
          >
            <Sparkles className="h-3 w-3" />
            {c.name}
            <span className="opacity-60">· {c.member_indicators.length}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function IndicatorCard({ indicator }) {
  const [open, setOpen] = useState(false);
  const token = BAND_TOKENS[indicator.confidence_band] || BAND_TOKENS.low;
  const pct = Math.round((indicator.normalized || 0) * 100);
  const Chev = open ? ChevronUp : ChevronDown;

  return (
    <div
      data-testid={`nutrient-indicator-${indicator.code}`}
      className={`rounded-2xl bg-white border ${token.cardAccentClass} p-4 sm:p-5 transition hover:shadow-sm`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-display text-base text-slate-900 leading-tight">{indicator.name}</div>
          <div className="text-xs text-slate-500 mt-0.5 font-mono">{indicator.code}</div>
        </div>
        <span
          data-testid={`nutrient-band-${indicator.code}`}
          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs border ${token.chipClass}`}
        >
          {token.label}
        </span>
      </div>

      {/* Score bar */}
      <div className="mt-4">
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>Strength</span>
          <span data-testid={`nutrient-score-${indicator.code}`}>{pct}%</span>
        </div>
        <div className="mt-1 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
          <div className={`h-full ${token.barClass} transition-all`} style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Compliance-safe language */}
      {indicator.compliance_language && (
        <p className="mt-3 text-sm text-slate-700 leading-relaxed">{indicator.compliance_language}</p>
      )}

      {/* Cluster pills */}
      {indicator.clusters?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {indicator.clusters.map((c) => (
            <span
              key={c}
              className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50 px-2 py-0.5 rounded-full border border-slate-200"
            >
              {c.replace(/_/g, " ")}
            </span>
          ))}
        </div>
      )}

      {/* Evidence trail */}
      {indicator.evidence_trail?.length > 0 && (
        <div className="mt-4 border-t border-slate-100 pt-3">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex items-center gap-1.5 text-xs text-purple-900 hover:text-purple-700"
            data-testid={`nutrient-evidence-toggle-${indicator.code}`}
          >
            <Chev className="h-3.5 w-3.5" />
            {open ? "Hide evidence trail" : `Show evidence trail (${indicator.evidence_trail.length})`}
          </button>
          {open && (
            <ul
              className="mt-2 space-y-1.5 text-xs text-slate-600"
              data-testid={`nutrient-evidence-trail-${indicator.code}`}
            >
              {indicator.evidence_trail.map((e, idx) => (
                <li key={`${e.field}-${idx}`} className="flex items-start gap-2">
                  <span className="mt-1.5 h-1 w-1 rounded-full bg-purple-300 shrink-0" />
                  <div className="min-w-0">
                    <span className="text-slate-800">{e.label}</span>
                    <span className="text-slate-400">
                      {" · "}field: <span className="font-mono">{e.field}</span> · weight {e.weight} · {e.source}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function ComplianceFooter() {
  return (
    <div
      className="mt-6 rounded-xl bg-slate-50 border border-slate-200 p-4 text-xs text-slate-600 leading-relaxed flex gap-3"
      data-testid="nutrient-compliance-footer"
    >
      <Info className="h-4 w-4 shrink-0 mt-0.5 text-slate-500" />
      <span>
        Wellness pattern signals are educational information only. They are not a medical
        diagnosis and do not replace clinical care. Review with a qualified practitioner.
      </span>
    </div>
  );
}

// ───────────────────── Main component ─────────────────────
export default function NutrientIndicatorsPanel({ clientId }) {
  const isPro = adminAuth.isPro();
  const [loading, setLoading] = useState(true);
  const [computing, setComputing] = useState(false);
  const [error, setError] = useState(null);
  const [assessmentId, setAssessmentId] = useState(null);
  const [results, setResults] = useState(null);
  const [escalation, setEscalation] = useState(null);
  const [routing, setRouting] = useState(null);
  const [featureFlagEnabled, setFeatureFlagEnabled] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Resolve latest Lite assessment for this client (single source of truth for nutrient compute).
      const { data: assessments } = await api.get(`/clients/${clientId}/assessments`);
      if (!assessments || assessments.length === 0) {
        setError("no_intake");
        setResults(null);
        setEscalation(null);
        setRouting(null);
        return;
      }
      const latest = assessments[0]; // server returns sorted by created_at desc
      setAssessmentId(latest.id);

      // Try to read existing nutrient artifacts in parallel; 404 means "not computed yet".
      const settle = await Promise.allSettled([
        api.get(`/nutrient/results/${latest.id}`),
        api.get(`/nutrient/escalation/${latest.id}`),
        api.get(`/nutrient/routing/${latest.id}`),
        api.get(`/nutrient/health`),
      ]);
      const [rRes, rEsc, rRoute, rHealth] = settle;

      if (rHealth.status === "fulfilled") {
        setFeatureFlagEnabled(!!rHealth.value.data?.feature_flag_enabled);
      }

      const isNotFound = (r) => r.status === "rejected" && r.reason?.response?.status === 404;
      const isForbidden = (r) => r.status === "rejected" && r.reason?.response?.status === 403;

      if (isForbidden(rRes) || isForbidden(rEsc) || isForbidden(rRoute)) {
        setError("forbidden");
        return;
      }
      if (isNotFound(rRes)) {
        setResults(null);
        setEscalation(null);
        setRouting(null);
        return;
      }
      if (rRes.status === "fulfilled") setResults(rRes.value.data);
      if (rEsc.status === "fulfilled") setEscalation(rEsc.value.data);
      if (rRoute.status === "fulfilled") setRouting(rRoute.value.data);
    } catch (err) {
      setError(formatApiError(err, "Could not load wellness indicators."));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (!isPro || !clientId) return;
    loadAll();
  }, [isPro, clientId, loadAll]);

  const triggerCompute = async () => {
    if (!assessmentId) return;
    setComputing(true);
    try {
      await api.post(`/nutrient/compute/${assessmentId}`);
      toast.success("Wellness patterns computed");
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err, "Compute failed"));
    } finally {
      setComputing(false);
    }
  };

  // Order indicators: routing.priority_order first, then remaining sorted by band.
  const orderedIndicators = useMemo(() => {
    if (!results?.indicators) return [];
    const priority = routing?.priority_order || [];
    const byCode = Object.fromEntries(results.indicators.map((i) => [i.code, i]));
    const priorityHits = priority.map((c) => byCode[c]).filter(Boolean);
    const seen = new Set(priorityHits.map((i) => i.code));
    const remaining = results.indicators
      .filter((i) => !seen.has(i.code))
      .sort(
        (a, b) =>
          (BAND_ORDER[a.confidence_band] ?? 9) - (BAND_ORDER[b.confidence_band] ?? 9) ||
          (b.normalized || 0) - (a.normalized || 0)
      );
    return [...priorityHits, ...remaining];
  }, [results, routing]);

  // ── Render gates ──
  if (!isPro) return null; // Lite tier: hide entirely (server also blocks).

  return (
    <section
      className="kcis-card p-6 sm:p-8 mb-6"
      data-testid="nutrient-indicators-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="kcis-overline text-purple-900">KCIS™ Pro · Wellness Patterns</div>
          <h2 className="font-display text-xl tracking-tight text-slate-900 mt-1">
            Nutrient &amp; Functional Imbalance Indicators
          </h2>
          <p className="text-sm text-slate-600 mt-1.5 max-w-2xl">
            Read-only pattern signals derived from the client's most recent intake. Confidence bands
            are wellness-language only — never diagnostic.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {results && (
            <Button
              variant="outline" size="sm"
              className="rounded-full border-slate-300 text-slate-700"
              onClick={loadAll}
              disabled={loading}
              data-testid="nutrient-refresh-btn"
            >
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              <span className="ml-1.5">Refresh</span>
            </Button>
          )}
        </div>
      </div>

      {loading && (
        <div className="py-12 flex items-center justify-center" data-testid="nutrient-loading">
          <Loader2 className="h-5 w-5 animate-spin text-purple-900" />
        </div>
      )}

      {!loading && error === "no_intake" && (
        <div
          className="mt-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm text-slate-600"
          data-testid="nutrient-empty-no-intake"
        >
          This client has no completed intake yet. Wellness pattern signals will appear once an intake is submitted.
        </div>
      )}

      {!loading && error === "forbidden" && (
        <div
          className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900"
          data-testid="nutrient-empty-forbidden"
        >
          Wellness pattern signals are a KCIS™ Pro feature. Your current edition does not include access.
        </div>
      )}

      {!loading && error && error !== "no_intake" && error !== "forbidden" && (
        <div
          className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-900"
          data-testid="nutrient-error"
        >
          {String(error)}
        </div>
      )}

      {!loading && !error && !results && assessmentId && (
        <div
          className="mt-4 rounded-xl border border-purple-200 bg-purple-50/50 p-6 text-sm text-slate-700"
          data-testid="nutrient-empty-not-computed"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <div className="font-display text-base text-slate-900">No wellness patterns computed yet.</div>
              <p className="text-sm text-slate-600 mt-1">
                Run the wellness pattern engine for this client's latest intake to surface indicator signals.
              </p>
              {!featureFlagEnabled && !adminAuth.isSuperAdmin() && (
                <p className="text-xs text-amber-800 mt-2">
                  The wellness pattern engine is currently disabled for this environment.
                </p>
              )}
            </div>
            <Button
              onClick={triggerCompute}
              disabled={computing || (!featureFlagEnabled && !adminAuth.isSuperAdmin())}
              className="rounded-full bg-purple-900 hover:bg-purple-800 text-white whitespace-nowrap"
              data-testid="nutrient-compute-btn"
            >
              {computing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              Compute now
            </Button>
          </div>
        </div>
      )}

      {!loading && results && (
        <div className="mt-5">
          <EscalationBanner escalation={escalation} />
          <ClusterStrip clusters={results.cluster_summaries} />
          <div
            className="grid grid-cols-1 lg:grid-cols-2 gap-4"
            data-testid="nutrient-indicator-grid"
          >
            {orderedIndicators.map((i) => (
              <IndicatorCard key={i.code} indicator={i} />
            ))}
          </div>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
            <span data-testid="nutrient-engine-version">
              Engine v{results.engine_version} · checksum {String(results.config_checksum || "").slice(0, 14)}…
            </span>
            <span>
              Computed {results.computed_at ? new Date(results.computed_at).toLocaleString() : "—"}
            </span>
          </div>
          <ComplianceFooter />
        </div>
      )}
    </section>
  );
}
